import type { ContentPageScannerCopy } from "./content-page-scanner-ssr.js";

export type SkillScannerFaqItem = {
  question: string;
  answer: string;
};

export type SkillScannerFinding = {
  severity: string;
  severityKey: "critical" | "high" | "medium" | "low";
  title: string;
  detail: string;
  evidence: string;
  confidence: string;
  location: string;
};

export type SkillScannerCopy = {
  metadata: {
    title: string;
    description: string;
    canonicalUrl: string;
    h1: string;
    robots: string;
  };
  navigation: {
    ariaLabel: string;
    report: string;
    checks: string;
    coverage: string;
    faq: string;
  };
  hero: {
    lead: string;
    trustLine: string;
    illustrationAlt: string;
  };
  scanner: ContentPageScannerCopy & {
    title: string;
    subtitle: string;
    staticOnlyLabel: string;
    staticReadOnlyBody: string;
  };
  report: {
    heading: string;
    summary: string;
    boundary: string;
    fixture: {
      ariaLabel: string;
      headerLabel: string;
      identifier: string;
      score: number;
      scoreLabel: string;
      decisionTitle: string;
      decisionBody: string;
      recommendationLabel: string;
      recommendationTitle: string;
      recommendationBody: string;
      summary: readonly { label: string; value: string }[];
      coverageTitle: string;
      coverageBody: string;
      findingsLabel: string;
      findings: readonly SkillScannerFinding[];
      exportAction: string;
      exportHint: string;
      shareAction: string;
      shareHint: string;
      pngAction: string;
      pngHint: string;
    };
  };
  checks: {
    heading: string;
    introduction: string;
    categories: readonly { title: string; body: string }[];
    evidenceLabel: string;
    evidenceBeforeOwasp: string;
    owaspLabel: string;
    evidenceBeforeCsa: string;
    csaLabel: string;
    evidenceBeforeSnyk: string;
    snykLabel: string;
    evidenceAfterSnyk: string;
  };
  coverage: {
    heading: string;
    introduction: string;
    limitationsLead: string;
    limitations: readonly string[];
    observableLabel: string;
    boundaryLabel: string;
  };
  faq: {
    heading: string;
    items: readonly SkillScannerFaqItem[];
  };
  explore: {
    heading: string;
    links: readonly {
      href: string;
      label: string;
      body: string;
      previewSrc: string;
      previewWidth: number;
      previewHeight: number;
    }[];
  };
  sources: {
    owasp: string;
    snyk: string;
    csa: string;
  };
};

export const skillScannerCopy = {
  metadata: {
    title: "AI Skill Scanner — Scan Agent Skills Before Installing",
    description:
      "Scan Agent Skills before installing. Detect prompt injection, data exfiltration, dangerous commands, and hidden payloads. Paste, upload, or submit a GitHub URL.",
    canonicalUrl: "https://skillsecurity.net/skill-scanner",
    h1: "AI Skill Scanner",
    robots: "index,follow",
  },
  navigation: {
    ariaLabel: "AI Skill Scanner",
    report: "Report",
    checks: "Checks",
    coverage: "Coverage",
    faq: "FAQ",
  },
  hero: {
    lead: "Scan an AI Agent Skill before you install it. Paste SKILL.md content, upload a file or zip workspace, or submit a public GitHub SKILL.md URL.",
    trustLine: "Reads submitted source as plain text. Scanned code is not executed.",
    illustrationAlt: "",
  },
  scanner: {
    title: "Scan a Skill",
    subtitle: "Add a SKILL.md source to begin.",
    ariaLabel: "AI Skill security scanner",
    inputLabel: "Skill Source",
    placeholder:
      "Paste SKILL.md content or a public GitHub URL such as https://github.com/org/repo/blob/main/SKILL.md",
    uploadLabel: "Upload",
    uploadHint: ".md or .zip",
    scanActionLabel: "Scan Skill",
    staticOnlyLabel: "Reads source as plain text:",
    staticReadOnlyBody: "nothing is executed.",
  },
  report: {
    heading: "See what a Skill scan report shows",
    summary:
      "A SkillSecurity report connects the Skill Safety Score and recommendation to the findings, evidence, file locations, and scan coverage behind them. Start with the highest-risk finding, then check what was scanned and skipped before deciding whether to install.",
    boundary:
      "Use the findings and coverage as evidence for your review. The score is not a certification or guarantee of safety.",
    fixture: {
      ariaLabel: "Example AI Skill security report",
      headerLabel: "Example report",
      identifier: "Anonymous Skill",
      score: 58,
      scoreLabel: "Skill Safety Score",
      decisionTitle: "Review before installing",
      decisionBody: "High-risk behavior was found in the submitted Skill workspace.",
      recommendationLabel: "Recommendation",
      recommendationTitle: "Inspect the evidence",
      recommendationBody:
        "Resolve the outbound credential access and review the incomplete coverage before installing.",
      summary: [
        { label: "Source", value: "Zip workspace" },
        { label: "Scope", value: "8 scanned / 1 skipped" },
        { label: "Scan mode", value: "Static archive scan" },
        { label: "Findings", value: "1 high / 2 medium" },
      ],
      coverageTitle: "Coverage needs review.",
      coverageBody:
        "One binary file was skipped and one referenced script could not be resolved from the submitted workspace.",
      findingsLabel: "Priority findings",
      findings: [
        {
          severity: "High",
          severityKey: "high",
          title: "Credential access followed by an outbound request",
          detail:
            "A bundled script reads an environment credential before sending data to an external endpoint.",
          evidence: "fetch(endpoint, { body: process.env.AGENT_TOKEN })",
          confidence: "High confidence",
          location: "scripts/telemetry.ts:48",
        },
      ],
      exportAction: "Export JSON",
      exportHint: "Download the full report",
      shareAction: "Copy share link",
      shareHint: "Share an unlisted report",
      pngAction: "Save PNG card",
      pngHint: "Export a visual summary",
    },
  },
  checks: {
    heading: "What an AI Skill scanner checks",
    introduction:
      "An AI Skill scanner reviews the instructions, scripts, configuration, and dependencies packaged with an Agent Skill before installation. It looks for patterns that could redirect agent behavior, expose data, misuse approved tools, or trigger unsafe actions.",
    categories: [
      {
        title: "Prompt injection",
        body: "Instructions that override expected behavior, suppress safeguards, or hide model directives.",
      },
      {
        title: "Credential access and data exfiltration",
        body: "Attempts to read secrets and send sensitive content to an external destination.",
      },
      {
        title: "Dangerous command or code execution",
        body: "Destructive shell actions, remote code fetches, install-time execution, and unsafe dynamic evaluation.",
      },
      {
        title: "Supply-chain and dependency risk",
        body: "Suspicious install hooks, unpinned external code, and risky dependency behavior.",
      },
      {
        title: "Excessive agency and permissions",
        body: "Tool, filesystem, shell, network, MCP, or delegated-agent access broader than the Skill's stated purpose.",
      },
      {
        title: "Obfuscation and hidden payloads",
        body: "Encoded commands, zero-width content, and concealed instructions or scripts.",
      },
    ],
    evidenceLabel: "Research context",
    evidenceBeforeOwasp: "These checks reflect the ",
    owaspLabel: "OWASP Agentic Skills Top 10",
    evidenceBeforeCsa: " and ",
    csaLabel: "CSA research on Agent Context Poisoning",
    evidenceBeforeSnyk: ". In a ",
    snykLabel: "February 2026 review of 3,984 public Skills from ClawHub and skills.sh",
    evidenceAfterSnyk:
      ", Snyk found 534 Skills that triggered a critical security policy and 76 Skills containing malicious payloads.",
  },
  coverage: {
    heading: "What static analysis can and cannot show",
    introduction:
      "Static analysis can identify known command patterns, credential-file references, outbound URLs, instruction overrides, obfuscation signatures, dependency signals, and suspicious relationships between those behaviors.",
    limitationsLead: "It cannot directly observe:",
    limitations: [
      "behavior that appears only while a Skill is running;",
      "encrypted or unfamiliar payloads that the current rule pack cannot analyze;",
      "vulnerabilities that depend on a specific agent platform or runtime version;",
      "files outside the submitted or eligible referenced scope.",
    ],
    observableLabel: "Observable static signals",
    boundaryLabel: "Coverage boundary",
  },
  faq: {
    heading: "Common questions",
    items: [
      {
        question: "What should I do if the scanner finds something?",
        answer:
          "Open the highest-severity finding and inspect its evidence, file location, and surrounding source. Review any skipped files or unresolved references as well. Do not install a Skill with unexplained critical or high-risk behavior; remove or resolve the risky behavior, then submit the revised source as a new scan.",
      },
      {
        question: "Is the scanner free?",
        answer:
          "Yes. The public scanner is free to use and does not require an account. Anonymous rate limits still apply.",
      },
      {
        question: "Can I scan a Skill from GitHub?",
        answer:
          "Yes. Submit a public GitHub URL that points to a SKILL.md file. The scan starts from that file and may include bounded files that it explicitly references from the same repository and commit. It does not discover an entire repository or folder. Upload a zip workspace when you need the supported multi-file archive scope.",
      },
      {
        question: "What does the Skill Safety Score mean?",
        answer:
          "The score runs from 0 to 100, with higher scores representing fewer or less severe risk signals in the content that was scanned. Read the recommendation, findings, evidence, and coverage with the score. Missing, skipped, and unscanned content is reported separately and is not included in the score calculation.",
      },
      {
        question: "Which Skill formats are supported?",
        answer:
          "SkillSecurity accepts SKILL.md content, single SKILL.md files, zip Skill workspaces, and supported GitHub SKILL.md URLs. If a Skill from another agent platform is packaged in one of those forms, it can be scanned.",
      },
      {
        question: "Can SkillSecurity scan MCP servers?",
        answer:
          "No. SkillSecurity scans SKILL.md and eligible bundled Skill files, not MCP servers. An included or referenced MCP configuration may appear in the report as a Skill-bundled configuration risk.",
      },
    ],
  },
  explore: {
    heading: "Explore SkillSecurity",
    links: [
      {
        href: "/claude-skill-security",
        label: "Claude Skill security review",
        body: "Claude-specific risk guidance and a manual review checklist.",
        previewSrc: "/img/skill-scanner-explore-claude.webp",
        previewWidth: 1200,
        previewHeight: 750,
      },
      {
        href: "/",
        label: "SkillSecurity home",
        body: "The SkillSecurity scanner and product overview.",
        previewSrc: "/img/skill-scanner-explore-home.webp",
        previewWidth: 1200,
        previewHeight: 750,
      },
    ],
  },
  sources: {
    owasp: "https://owasp.org/www-project-agentic-skills-top-10/",
    snyk: "https://snyk.io/blog/toxicskills-malicious-ai-agent-skills-clawhub/",
    csa: "https://labs.cloudsecurityalliance.org/research/csa-research-note-skill-md-agent-context-poisoning-20260506/",
  },
} as const satisfies SkillScannerCopy;
